import { useEffect, useRef, useState } from 'react';

import { type EventsType, type IIdleTimer, useIdleTimer } from 'react-idle-timer';

import auth from '../auth';

import { beginLocalLogout, clearLocalAuthSession } from './authSession';
import { type LogoutOwnershipDependencies, runLogoutOwnership } from './logoutOwnership';
import { parseSessionLifetime } from './sessionLifetime';

export type SessionLogoutReason = 'idle' | 'manual';

interface SessionLogoutMessage {
  type: 'logout';
  reason: SessionLogoutReason;
}
type SessionIdleDiagnostic =
  | { type: 'cross-tab-fallback' }
  | { type: 'invalid-lifetime'; reason: 'missing' | 'non-finite' | 'non-positive' };

export interface SessionIdleOptions {
  crossTab?: boolean;
  lifecycleKey?: string;
  timeoutSeconds?: unknown;
  onDiagnostic?: (diagnostic: SessionIdleDiagnostic) => void;
  logoutOwnership?: LogoutOwnershipDependencies;
  leaveProtectedUi?: () => void;
}

export const DEC_SESSION_IDLE_OPTIONS = {
  name: 'dec-session-idle-v1',
  crossTab: true,
  syncTimers: 200,
  leaderElection: true,
  events: [
    'keydown',
    'pointerdown',
    'pointermove',
    'mousedown',
    'mousemove',
    'wheel',
    'touchstart',
    'touchmove'
  ] as Array<EventsType>
};

let activeLogoutRequest:
  { lease: symbol; request: (reason: SessionLogoutReason) => void } | undefined;

const LOGOUT_LOCK_NAME = 'dec-session-logout-v1';
const LOGOUT_LEASE_KEY = 'dec:session-logout-v1';

export function leaveProtectedUi() {
  if (window.location.pathname !== '/auth/callback') window.location.replace('/login');
}

function browserOwnershipDependencies(): LogoutOwnershipDependencies {
  return {
    locks: navigator.locks as unknown as LogoutOwnershipDependencies['locks'],
    storage: window.localStorage
  };
}

function isLogoutMessage(value: unknown): value is SessionLogoutMessage {
  if (!value || typeof value !== 'object') return false;
  const message = value as Record<string, unknown>;
  return (
    Object.keys(message).length === 2 &&
    message.type === 'logout' &&
    typeof message.reason === 'string' &&
    ['idle', 'manual'].includes(message.reason)
  );
}

export function requestSessionLogout(reason: SessionLogoutReason) {
  activeLogoutRequest?.request(reason);
}

export function useSessionIdleCoordinator(
  lifetime: unknown,
  authenticated: boolean,
  options: SessionIdleOptions = {}
) {
  const parsed = parseSessionLifetime(lifetime, options.timeoutSeconds);
  const enabled = parsed.enabled;
  const timeoutMs = enabled ? parsed.timeoutMs : 0;
  const promptBeforeIdleMs = enabled ? parsed.promptBeforeIdleMs : 0;
  const invalidReason = enabled ? undefined : parsed.reason;
  const timer = useRef<IIdleTimer | undefined>(undefined);
  const stale = useRef(true);
  const loggingOut = useRef(false);
  const [prompted, setPrompted] = useState(false);
  const [leader, setLeader] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const lease = useRef(Symbol('session-idle')).current;
  const onDiagnostic = useRef(options.onDiagnostic);
  onDiagnostic.current = options.onDiagnostic;
  const crossTab = options.crossTab !== false;
  const lifecycleKey = options.lifecycleKey;
  const leaveUi = options.leaveProtectedUi ?? leaveProtectedUi;

  const closePrompt = () => {
    setPrompted(false);
    setRemainingSeconds(0);
  };

  const showPrompt = () => {
    if (!stale.current) {
      setRemainingSeconds(Math.ceil((timer.current?.getRemainingTime() ?? 0) / 1_000));
      setPrompted(true);
      setLeader(crossTab ? timer.current?.isLeader() === true : true);
    }
  };

  const logout = (reason: SessionLogoutReason, broadcast: boolean) => {
    if (stale.current || loggingOut.current) return;
    loggingOut.current = true;
    if (broadcast) timer.current?.message({ type: 'logout', reason }, true);
    void runLogoutOwnership(
      LOGOUT_LOCK_NAME,
      LOGOUT_LEASE_KEY,
      options.logoutOwnership ?? browserOwnershipDependencies(),
      {
        onFollower: () => {
          beginLocalLogout();
          closePrompt();
          timer.current?.pause();
          leaveUi();
        },
        onOwner: async () => {
          beginLocalLogout();
          closePrompt();
          timer.current?.pause();
          try {
            await auth.logout();
          } catch {
            clearLocalAuthSession();
            leaveUi();
          }
        }
      }
    );
  };

  // The hook must be called on every render; disabled prevents an active timer.
  const idleTimer = useIdleTimer({
    ...DEC_SESSION_IDLE_OPTIONS,
    timeout: timeoutMs,
    promptBeforeIdle: promptBeforeIdleMs,
    crossTab,
    syncTimers: crossTab ? DEC_SESSION_IDLE_OPTIONS.syncTimers : 0,
    leaderElection: crossTab,
    disabled: !authenticated || !enabled,
    onPrompt: showPrompt,
    onActive: closePrompt,
    onIdle: () => logout('idle', true),
    onMessage: (message) => {
      if (isLogoutMessage(message)) logout(message.reason, false);
    }
  });
  timer.current = idleTimer;

  useEffect(() => {
    if (!authenticated) return;

    stale.current = false;
    loggingOut.current = false;
    const registration = { lease, request: (reason: SessionLogoutReason) => logout(reason, true) };
    activeLogoutRequest = registration;

    const cleanup = () => {
      stale.current = true;
      closePrompt();
      timer.current?.pause();
      if (activeLogoutRequest === registration) activeLogoutRequest = undefined;
      loggingOut.current = false;
    };

    if (!enabled) return cleanup;

    if (!crossTab) onDiagnostic.current?.({ type: 'cross-tab-fallback' });

    return cleanup;
  }, [authenticated, crossTab, enabled, lifecycleKey, promptBeforeIdleMs, timeoutMs]);

  useEffect(() => {
    if (invalidReason) onDiagnostic.current?.({ type: 'invalid-lifetime', reason: invalidReason });
  }, [invalidReason]);

  useEffect(() => {
    if (!authenticated || !enabled) return;
    const observeTimer = () => {
      const currentTimer = timer.current;
      setPrompted(currentTimer?.isPrompted() === true);
      setLeader(crossTab ? currentTimer?.isLeader() === true : true);
    };
    const interval = window.setInterval(observeTimer, DEC_SESSION_IDLE_OPTIONS.syncTimers);
    return () => window.clearInterval(interval);
  }, [authenticated, crossTab, enabled, lifecycleKey]);

  useEffect(() => {
    if (!prompted) return;
    const interval = window.setInterval(() => {
      setRemainingSeconds(Math.ceil((timer.current?.getRemainingTime() ?? 0) / 1_000));
    }, 1_000);
    return () => window.clearInterval(interval);
  }, [prompted]);

  return {
    prompted: prompted && leader,
    remainingSeconds,
    continueSession: () => {
      timer.current?.activate();
      closePrompt();
    },
    signOut: () => logout('manual', true)
  };
}
